var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';
import ImageUploader from '../ImageUploader/ImageUploader';
import { ImageType } from '@common/contracts';
import { getNodeImages, uploadNodeImage, deleteNodeImage, getArchivedNodeImages, restoreNodeImage } from '../../api/images';
import { assetUrl } from '../../utils/img';
import { getCoordinateString, parseCoordinateString } from '../../utils/coordinates';
import './MapArtGrid.scss';
function MapArtGrid({ levelId, mapWidth, mapHeight, onImagesUpdated, refreshTrigger, onImageChange }) {
    const [gridData, updateGridData] = useImmer([]);
    const [selectedCell, setSelectedCell] = useState(null);
    const [pendingUploadCell, setPendingUploadCell] = useState(null);
    // Deleted (archived) cell art, keyed "x,y" — offered for restore when a cell has no active image.
    const [archivedByCoord, setArchivedByCoord] = useState({});
    const fileInputRef = useRef(null);
    // Initialize grid data
    useEffect(() => {
        const newGrid = [];
        for (let y = 0; y < mapHeight; y++) {
            const row = [];
            for (let x = 0; x < mapWidth; x++) {
                row.push({ x, y });
            }
            newGrid.push(row);
        }
        updateGridData(() => newGrid);
    }, [mapWidth, mapHeight, updateGridData]);
    // Load or reload images from API
    useEffect(() => {
        const loadImages = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const [images, archived] = yield Promise.all([
                    getNodeImages(levelId),
                    getArchivedNodeImages(levelId),
                ]);
                const activeCoords = new Set(images.map(i => i.coord));
                updateGridData(draft => {
                    images.forEach(image => {
                        const pos = parseCoordinateString(image.coord);
                        if (pos && draft[pos.y] && draft[pos.y][pos.x]) {
                            draft[pos.y][pos.x].image = image;
                        }
                    });
                });
                // Keep only archived slots that don't have a live image (those are restorable).
                const arch = {};
                for (const im of archived) {
                    if (!activeCoords.has(im.coord))
                        arch[im.coord] = im;
                }
                setArchivedByCoord(arch);
            }
            catch (error) {
                console.error('Error loading node images:', error);
            }
        });
        if (gridData.length > 0) {
            loadImages();
        }
    }, [levelId, updateGridData, gridData.length, refreshTrigger]);
    const handleCellClick = (x, y) => {
        var _a, _b;
        const cellData = (_a = gridData[y]) === null || _a === void 0 ? void 0 : _a[x];
        // If cell has an image — or a deleted one that can be restored — open the modal.
        if ((cellData === null || cellData === void 0 ? void 0 : cellData.image) || archivedByCoord[getCoordinateString(x, y)]) {
            setSelectedCell({ x, y });
        }
        else {
            // If cell is empty, open file dialog directly
            setPendingUploadCell({ x, y });
            (_b = fileInputRef.current) === null || _b === void 0 ? void 0 : _b.click();
        }
    };
    // Un-archive a deleted cell image (no re-upload) and put it back on the map.
    const handleImageRestore = (x, y) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        const arch = archivedByCoord[getCoordinateString(x, y)];
        if (!arch)
            return;
        try {
            const restored = (_a = (yield restoreNodeImage(arch.id))) !== null && _a !== void 0 ? _a : arch;
            updateGridData(draft => {
                if (draft[y] && draft[y][x])
                    draft[y][x].image = restored;
            });
            setArchivedByCoord(prev => {
                const next = Object.assign({}, prev);
                delete next[`${x},${y}`];
                return next;
            });
            onImageChange === null || onImageChange === void 0 ? void 0 : onImageChange(x, y, restored.imageUrl);
            if (onImagesUpdated)
                onImagesUpdated(gridData.flat().filter(c => c.image).length + 1);
        }
        catch (error) {
            console.error('Error restoring image:', error);
        }
        closeUploader();
    });
    const handleFileSelect = (event) => {
        var _a;
        const file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (file && pendingUploadCell) {
            handleImageUpload(file, pendingUploadCell.x, pendingUploadCell.y);
            setPendingUploadCell(null);
        }
        // Reset input value to allow selecting the same file again
        if (event.target) {
            event.target.value = '';
        }
    };
    const handleImageUpload = (file, x, y) => __awaiter(this, void 0, void 0, function* () {
        // Set uploading state
        updateGridData(draft => {
            if (draft[y] && draft[y][x]) {
                draft[y][x].isUploading = true;
            }
        });
        try {
            // Upload image to backend
            const coord = getCoordinateString(x, y);
            const response = yield uploadNodeImage({
                levelId,
                coord,
                imageType: ImageType.MapNode,
                file
            });
            if (response.success && response.imageUrl) {
                const uploadedImage = {
                    id: `${levelId}:${coord}`,
                    levelId,
                    coord,
                    imageUrl: response.imageUrl,
                    imageType: ImageType.MapNode,
                    filename: response.filename || file.name,
                    uploadedAt: new Date()
                };
                // Update grid with uploaded image
                updateGridData(draft => {
                    if (draft[y] && draft[y][x]) {
                        draft[y][x].image = uploadedImage;
                        draft[y][x].isUploading = false;
                    }
                });
                if (onImagesUpdated) {
                    // Recalculate count quickly
                    const uploaded = gridData.flat().filter(c => c.image || (c.y === y && c.x === x)).length;
                    onImagesUpdated(uploaded);
                }
                if (onImageChange) {
                    onImageChange(x, y, uploadedImage.imageUrl);
                }
            }
            else {
                throw new Error(response.error || 'Upload failed');
            }
        }
        catch (error) {
            console.error('Error uploading image:', error);
            // Reset uploading state on error
            updateGridData(draft => {
                if (draft[y] && draft[y][x]) {
                    draft[y][x].isUploading = false;
                }
            });
        }
    });
    const handleImageRemove = (x, y) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const currentImage = (_b = (_a = gridData[y]) === null || _a === void 0 ? void 0 : _a[x]) === null || _b === void 0 ? void 0 : _b.image;
        if (currentImage) {
            try {
                // Remove from backend if it has an ID
                if (currentImage.id) {
                    yield deleteNodeImage(currentImage.id);
                    // Keep the (soft-deleted) image around so it can be restored this session.
                    if (!currentImage.imageUrl.startsWith('blob:')) {
                        setArchivedByCoord(prev => (Object.assign(Object.assign({}, prev), { [`${x},${y}`]: currentImage })));
                    }
                }
                // Clean up blob URL if needed
                if (currentImage.imageUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(currentImage.imageUrl);
                }
                // Update local state
                updateGridData(draft => {
                    if (draft[y] && draft[y][x]) {
                        draft[y][x].image = undefined;
                    }
                });
                if (onImagesUpdated) {
                    const uploaded = gridData.flat().filter(c => c.image && !(c.x === x && c.y === y)).length;
                    onImagesUpdated(uploaded);
                }
                if (onImageChange) {
                    onImageChange(x, y, null);
                }
            }
            catch (error) {
                console.error('Error removing image:', error);
                // Still remove from local state even if backend removal fails
                updateGridData(draft => {
                    if (draft[y] && draft[y][x]) {
                        draft[y][x].image = undefined;
                    }
                });
            }
        }
    });
    const closeUploader = () => {
        setSelectedCell(null);
    };
    const renderGridCell = (cellData) => {
        const { x, y, image, isUploading } = cellData;
        const letter = String.fromCharCode('A'.charCodeAt(0) + x);
        const cellIndex = `${letter}${y + 1}`;
        if (isUploading) {
            return (_jsx("div", { className: "map-art-cell uploading", children: _jsx("div", { className: "upload-spinner", children: "\u23F3" }) }, cellIndex));
        }
        if (image) {
            return (_jsxs("div", { className: "map-art-cell has-image", onClick: () => handleCellClick(x, y), children: [_jsx("img", { src: assetUrl(image.imageUrl), alt: `Node ${cellIndex}`, className: "cell-image-preview" }), _jsxs("div", { className: "cell-overlay", children: [_jsx("span", { className: "cell-index", children: cellIndex }), _jsx("span", { className: "cell-upload-hint", children: "Click to adjust" })] })] }, cellIndex));
        }
        return (_jsxs("div", { className: "map-art-cell empty", onClick: () => handleCellClick(x, y), children: [_jsx("span", { className: "cell-index", children: cellIndex }), _jsx("span", { className: "cell-upload-hint", children: "Click to upload" })] }, cellIndex));
    };
    return (_jsxs("div", { className: "map-art-grid", children: [_jsx("div", { className: "grid-container", style: {
                    gridTemplateColumns: `repeat(${mapWidth}, 1fr)`
                }, children: gridData.flat().map(cellData => renderGridCell(cellData)) }), _jsx("input", { ref: fileInputRef, type: "file", accept: "image/*", onChange: handleFileSelect, style: { display: 'none' } }), selectedCell && (() => {
                var _a, _b;
                const active = (_b = (_a = gridData[selectedCell.y]) === null || _a === void 0 ? void 0 : _a[selectedCell.x]) === null || _b === void 0 ? void 0 : _b.image;
                const archived = archivedByCoord[getCoordinateString(selectedCell.x, selectedCell.y)];
                const restorable = !active && !!archived;
                return (_jsx(ImageUploader, { isOpen: true, onClose: closeUploader, onUpload: (file) => handleImageUpload(file, selectedCell.x, selectedCell.y), onRemove: active ? () => handleImageRemove(selectedCell.x, selectedCell.y) : undefined, currentImage: active !== null && active !== void 0 ? active : archived, restorable: restorable, onRestore: restorable ? () => handleImageRestore(selectedCell.x, selectedCell.y) : undefined, title: `Upload Image for Cell ${String.fromCharCode('A'.charCodeAt(0) + selectedCell.x)}${selectedCell.y + 1}` }));
            })()] }));
}
export default MapArtGrid;
